<template>
  <div class="print-diet-modal__settings">
    <div class="print-diet-modal__row">
      <Text size="text-sm" color="color-text-text-secondary-700" weight="semibold">
        Szablon wydruku
      </Text>
      <div class="print-diet-modal__template-control">
        <Button
          aria-label="Otwórz bibliotekę szablonów PDF"
          color="gray"
          size="sm"
          variant="secondary"
          @click="emit('preview')"
        >
          {{ selectedThemeLabel }}
          <template #rightIcon><Icon name="Edit05" /></template>
        </Button>
      </div>
    </div>
    <div class="print-diet-modal__row">
      <Text size="text-sm" color="color-text-text-secondary-700" weight="semibold">
        Sortuj po
        <HelperTooltip
          content="Określa kolejność składników w przepisach na wydruku (malejąco po gramaturze składnika lub alfabetycznie A–Z)."
        />
      </Text>
      <ButtonGroup color="gray">
        <Button
          :data-ui-state="options.sortBy === 'grams' ? 'pressed' : undefined"
          :disabled="!enabled"
          @click="emit('option', 'sortBy', 'grams')"
        >
          Gramaturze
        </Button>
        <Button
          :data-ui-state="
            options.sortBy === 'alphabetically' ? 'pressed' : undefined
          "
          :disabled="!enabled"
          @click="emit('option', 'sortBy', 'alphabetically')"
        >
          Nazwie
        </Button>
      </ButtonGroup>
    </div>
    <div class="print-diet-modal__row">
      <Text size="text-sm" color="color-text-text-secondary-700" weight="semibold">
        Grupuj po
        <HelperTooltip
          content="Określa układ wydruku PDF. Dni grupują plan wg chronologii (Dzień 1, Dzień 2 itd.), a Posiłki wg typu dań (najpierw śniadania, potem obiady itd.)."
        />
      </Text>
      <ButtonGroup color="gray">
        <Button
          :data-ui-state="options.groupBy === 'days' ? 'pressed' : undefined"
          :disabled="!enabled"
          @click="emit('groupBy', 'days')"
        >
          Dniach
        </Button>
        <Button
          :data-ui-state="options.groupBy === 'meals' ? 'pressed' : undefined"
          :disabled="!enabled"
          @click="emit('groupBy', 'meals')"
        >
          Posiłkach
        </Button>
      </ButtonGroup>
    </div>
    <div class="print-diet-modal__row">
      <Text size="text-sm" color="color-text-text-secondary-700" weight="semibold">
        Każdy{{ options.groupBy === "days" ? " dzień" : " posiłek" }}
        na osobnej stronie
        <HelperTooltip
          :content="`Każda sekcja (${options.groupBy === 'days' ? 'dzień' : 'posiłek'}) zaczyna się od nowej strony. Zapewnia to przejrzysty układ dla pacjenta, ale znacznie zwiększa długość PDF.`"
        />
      </Text>
      <ButtonGroup color="gray">
        <Button
          :data-ui-state="!options.separateDaysOrMeals ? 'pressed' : undefined"
          :disabled="!enabled"
          @click="emit('option', 'separateDaysOrMeals', false)"
        >
          Nie
        </Button>
        <Button
          :data-ui-state="options.separateDaysOrMeals ? 'pressed' : undefined"
          :disabled="!enabled"
          @click="emit('option', 'separateDaysOrMeals', true)"
        >
          Tak
        </Button>
      </ButtonGroup>
    </div>
    <div class="print-diet-modal__row">
      <Text size="text-sm" color="color-text-text-secondary-700" weight="semibold">
        Format wyświetlania dni
        <HelperTooltip
          content="Numeryczny prezentuje dni jako Dzień 1, Dzień 2 itd. Tygodniowy wyświetla dni tygodnia (np. Poniedziałek, Wtorek), a w przypadku jadłospisów z przypisanymi datami pokazuje pełną datę (np. Poniedziałek 03.08.2026)."
        />
      </Text>
      <ButtonGroup color="gray">
        <Button
          :data-ui-state="dayFormat === 'numeric' ? 'pressed' : undefined"
          :disabled="!enabled"
          @click="emit('dayFormat', 'numeric')"
        >
          Numeryczny
        </Button>
        <Button
          :data-ui-state="dayFormat === 'days_of_week' ? 'pressed' : undefined"
          :disabled="!enabled"
          @click="emit('dayFormat', 'days_of_week')"
        >
          Tygodniowy
        </Button>
      </ButtonGroup>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { computed } from 'vue'

  import { Button, ButtonGroup, HelperTooltip, Icon } from '@/components/ui'
  import Text from '@/components/ui/Text/Text.vue'

  import type { DayFormat, GroupBy, PrintOptions, PrintTheme } from '../../types'

  const props = defineProps<{
    dayFormat: DayFormat
    enabled: boolean
    options: PrintOptions
    themes: PrintTheme[]
  }>()
  const emit = defineEmits<{
    dayFormat: [value: DayFormat]
    groupBy: [value: GroupBy]
    option: [key: 'separateDaysOrMeals' | 'sortBy', value: boolean | PrintOptions['sortBy']]
    preview: []
  }>()

  const selectedThemeLabel = computed(
    () => props.themes.find(theme => theme.id === props.options.theme)?.label ?? 'Wybierz szablon',
  )
</script>
